import {
  User,
  browserLocalPersistence,
  browserSessionPersistence,
  onAuthStateChanged,
  setPersistence,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import React, {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { auth } from "../../firebaseConfig";

type AdminAuthContextValue = {
  ready: boolean;
  user: User | null;
  error: string;
  login: (email: string, password: string, remember: boolean) => Promise<User>;
  logout: () => Promise<void>;
  getIdToken: (forceRefresh?: boolean) => Promise<string>;
};

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

/**
 * 관리자 인증 상태 컨텍스트 제공자.
 * 로그인/로그아웃/토큰 발급 함수를 화면 전체에서 공유한다.
 */
export function AdminAuthProvider({ children }: PropsWithChildren) {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    // 관리자 화면도 Firebase Auth의 실시간 로그인 상태를 그대로 따른다.
    const unsubscribe = onAuthStateChanged(
      auth,
      (nextUser) => {
        setUser(nextUser);
        setReady(true);
      },
      (authError) => {
        setError(authError.message || "Firebase auth init failed");
        setReady(true);
      },
    );

    return unsubscribe;
  }, []);

  const value = useMemo<AdminAuthContextValue>(
    () => ({
      ready,
      user,
      error,
      async login(email: string, password: string, remember: boolean) {
        // 로그인 유지 여부만 프런트에서 정하고, 실제 인증은 Firebase가 처리한다.
        await setPersistence(auth, remember ? browserLocalPersistence : browserSessionPersistence);
        const credential = await signInWithEmailAndPassword(auth, email.trim(), password);
        setUser(credential.user);
        return credential.user;
      },
      async logout() {
        await signOut(auth);
        setUser(null);
      },
      async getIdToken(forceRefresh = false) {
        // 관리자 API 호출은 모두 이 ID 토큰을 Authorization 헤더로 전달한다.
        if (!auth.currentUser) return "";
        return auth.currentUser.getIdToken(forceRefresh);
      },
    }),
    [error, ready, user],
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

// AdminAuthContext를 안전하게 읽는 전용 훅이다.
export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error("useAdminAuth must be used inside AdminAuthProvider");
  }
  return context;
}
